import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icons as I } from '../pack-icons';
import { useActivity } from './ActivityProvider';

/**
 * ActivityPill — global topbar status indicator + expanded panel + done toast.
 * Reads from ActivityContext. Renders nothing when activity == null.
 */
function ActivityToast({ data, onView }) {
  const { t } = useTranslation();
  return (
    <div className="act-toast" role="status">
      <span className="act-toast-ico">
        <I.check size={14} />
      </span>
      <span className="act-toast-msg">{data.message || t('activity.done')}</span>
      {onView && (
        <button className="act-toast-link" type="button" onClick={onView}>
          {t('activity.view')}
        </button>
      )}
    </div>
  );
}

export default function ActivityPill() {
  const { t } = useTranslation();
  const { activity, setActivity } = useActivity();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const ref = useRef(null);
  const prevKind = useRef(null);

  const openBatch = (batchId) => {
    if (!batchId) return;
    setOpen(false);
    navigate(`/app/batch/${batchId}`);
  };

  // Auto-dismiss any 'done' activity after 4.2s. Two arrival paths land here:
  //   (a) active→done transition — long batch generation finished
  //   (b) direct 'done' without prior 'active' — quick saves that call
  //       setActivity({ kind: 'done', message: 'Post saved' }) without
  //       ever being in an active state (EditPost save, ContentPlan
  //       update, EditStrategy save, "Slide deleted", etc.)
  // The previous version gated the timer on `prevKind.current === 'active'`,
  // which meant path (b) never armed the timeout and the toast hung on
  // screen forever. We now treat every 'done' the same — the timer fires
  // and clears activity (and toast), regardless of what came before.
  useEffect(() => {
    if (!activity) return;
    if (activity.kind === 'done') {
      setToast(activity);
      const t = setTimeout(() => {
        setToast(null);
        setActivity(null);
      }, 4200);
      return () => clearTimeout(t);
    }
    prevKind.current = activity.kind;
  }, [activity, setActivity]);

  // Close panel on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (!activity) {
    return toast ? (
      <ActivityToast
        data={toast}
        onView={toast.batchId ? () => openBatch(toast.batchId) : null}
      />
    ) : null;
  }
  if (activity.kind === 'done') {
    return (
      <ActivityToast
        data={activity}
        onView={activity.batchId ? () => openBatch(activity.batchId) : null}
      />
    );
  }

  const pct =
    activity.kind === 'active' && activity.total > 0
      ? Math.round((activity.current / activity.total) * 100)
      : null;

  // Pill body opens the batch detail screen directly — most users want
  // "show me what's happening" in one click. The chevron next to the body
  // still toggles the inline drop-down for the rare power-user who wants
  // the full activity panel without leaving the current page.
  // `returnTo` is set by callers like EditPost that want the pill to link
  // back to a SPECIFIC page (not a batch) — image regen needs to land
  // back on /app/post/:id/edit?tab=media. Takes priority over batchId.
  const pillBodyAction = activity.returnTo
    ? () => { setOpen(false); navigate(activity.returnTo); }
    : activity.batchId
    ? () => openBatch(activity.batchId)
    : () => setOpen((o) => !o);

  // Does the activity have numerical progress to show? Batch generations
  // do (current/total/%); ad-hoc tasks like image regen, manual edits, or
  // strategy saves don't — they're step-only. When numbers are absent we
  // render the step text instead of an empty "Generating of " label.
  const hasNumbers = activity.kind === 'active'
    && typeof activity.current === 'number'
    && typeof activity.total === 'number'
    && activity.total > 0;

  return (
    <>
      <div className={`act-pill ${open ? 'is-open' : ''}`} ref={ref}>
        <button className="act-pill-btn" onClick={pillBodyAction} type="button">
          <span className="act-spinner" aria-hidden>
            <span className="dot pulse" />
          </span>
          <span className="act-label">
            {activity.kind === 'planning' ? (
              <>{t('activity.updatingPlan')} · <b>{activity.step}</b></>
            ) : hasNumbers ? (
              <>{t('activity.generating')} <b>{activity.current} {t('activity.of')} {activity.total}</b></>
            ) : (
              <><b>{activity.step || t('activity.working')}</b></>
            )}
          </span>
          {pct != null && (
            <span className="act-prog" aria-hidden>
              <span className="act-prog-fill" style={{ width: pct + '%' }} />
            </span>
          )}
          <span
            role="button"
            tabIndex={0}
            aria-label={t('activity.toggleDetails')}
            className="act-pill-toggle"
            onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); } }}
            style={{ display: 'inline-flex', cursor: 'pointer' }}
          >
            <I.chevDown size={12} />
          </span>
        </button>

        <div className="act-panel" role="dialog" aria-label={t('activity.title')}>
          <div className="act-panel-h">
            <div>
              <div className="eyebrow" style={{ marginBottom: 6 }}>
                <span>{activity.kind === 'planning' ? t('activity.contentPlanUpdate') : t('activity.batchGeneration')}</span>
              </div>
              <div className="act-title">{activity.project}</div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)} type="button">
              <I.x size={13} />
            </button>
          </div>

          {activity.kind === 'active' && (
            <>
              <div className="act-meta">
                <div>
                  <div className="act-meta-l">{t('activity.currentStep')}</div>
                  <div className="act-meta-v">
                    <span className="dot pulse" style={{ background: 'var(--accent)' }} /> {activity.step}
                  </div>
                </div>
                {hasNumbers && (
                  <div>
                    <div className="act-meta-l">{t('activity.progress')}</div>
                    <div className="act-meta-v">
                      <b>{activity.current}</b> {t('activity.of')} {activity.total}
                    </div>
                  </div>
                )}
                {activity.eta && (
                  <div>
                    <div className="act-meta-l">{t('activity.eta')}</div>
                    <div className="act-meta-v">{activity.eta}</div>
                  </div>
                )}
              </div>

              {hasNumbers && (
                <div className="act-progress">
                  <div className="act-progress-track">
                    <div className="act-progress-fill" style={{ width: pct + '%' }} />
                  </div>
                  <span className="mono act-pct">{pct}%</span>
                </div>
              )}

              {activity.items && (
                <div className="act-items">
                  {activity.items.slice(0, 6).map((it, i) => (
                    <div key={i} className={`act-item act-item-${it.status}`}>
                      <span className="act-item-ico">
                        {it.status === 'done' && <I.check size={11} />}
                        {it.status === 'doing' && <span className="dot pulse" style={{ background: 'currentColor' }} />}
                        {it.status === 'queued' && <span className="dot" style={{ background: 'currentColor', opacity: 0.5 }} />}
                      </span>
                      <span className="act-item-l">{it.label}</span>
                      <span className="act-item-r mono">{it.meta}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="act-foot">
                {activity.failed > 0 && (
                  <span className="mono" style={{ fontSize: 11, color: 'var(--danger-text)' }}>
                    {t('activity.failed', { count: activity.failed })}
                  </span>
                )}
                <span style={{ flex: 1 }} />
                {activity.returnTo ? (
                  <button
                    className="btn btn-soft btn-sm"
                    type="button"
                    onClick={() => { setOpen(false); navigate(activity.returnTo); }}
                  >
                    {activity.returnLabel || t('activity.open')} <I.arrowRight size={12} />
                  </button>
                ) : activity.batchId ? (
                  <button
                    className="btn btn-soft btn-sm"
                    type="button"
                    onClick={() => openBatch(activity.batchId)}
                  >
                    {t('activity.openBatch')} <I.arrowRight size={12} />
                  </button>
                ) : null}
              </div>
            </>
          )}

          {activity.kind === 'planning' && (
            <div style={{ padding: '18px 0 6px', color: 'var(--text-muted)', fontSize: 13 }}>
              {activity.step}
            </div>
          )}
        </div>
      </div>
      {toast && <ActivityToast data={toast} />}
    </>
  );
}
